import type { Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { useEffect, useReducer, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cssColorToHex } from "./cueRichText";
import { SHOW_ROW_SPAN_UI } from "./rowSpanFeature";

const DEFAULT_INK = "#1a1a1a";

/** Fixed ink colors — no free mixer. Black first = plain body ink. */
export const TEXT_PRESET_COLORS = [
  DEFAULT_INK,
  "#9a4d2e",
  "#2c5f4a",
  "#1e4d8c",
  "#8b1a3a",
  "#7a4e00",
  "#5b3d8f",
  "#0f6b6b",
  "#4a4a4a",
] as const;

const PRESET_COLOR_LABELS: Record<string, string> = {
  [DEFAULT_INK]: "Sort (almindelig tekst)",
};

/** True if any textStyle color mark exists in the selection (incl. mixed). */
function selectionHasColorMark(editor: Editor): boolean {
  if (editor.isDestroyed || !editor.schema) return false;
  const type = editor.schema.marks.textStyle;
  if (!type) return false;
  const { from, to, empty } = editor.state.selection;
  if (empty) {
    const marks =
      editor.state.storedMarks ?? editor.state.selection.$from.marks();
    return marks.some(
      (m) => m.type === type && Boolean(cssColorToHex(m.attrs.color)),
    );
  }
  let found = false;
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (found || !node.isText) return !found;
    const mark = type.isInSet(node.marks);
    if (mark && cssColorToHex(mark.attrs.color)) found = true;
    return !found;
  });
  return found;
}

/**
 * Apply or clear color over an explicit range. TipTap setColor/unsetColor rely
 * on the live selection; when that is lost to the toolbar — or when the
 * selection spans mixed colors — remove+add on the saved range rewrites every
 * mark reliably.
 */
function applyColorToRange(
  editor: Editor,
  range: { from: number; to: number },
  color: string | null,
) {
  if (editor.isDestroyed || !editor.schema) return;
  const type = editor.schema.marks.textStyle;
  if (!type) return;
  editor
    .chain()
    .focus()
    .command(({ tr }) => {
      const docSize = tr.doc.content.size;
      const from = Math.max(0, Math.min(range.from, docSize));
      const to = Math.max(from, Math.min(range.to, docSize));
      tr.setSelection(TextSelection.create(tr.doc, from, to));
      if (from === to) {
        if (color) tr.addStoredMark(type.create({ color }));
        else tr.removeStoredMark(type);
        return true;
      }
      tr.removeMark(from, to, type);
      if (color) tr.addMark(from, to, type.create({ color }));
      return true;
    })
    .run();
}

export interface FormatToolbarProps {
  editor: Editor | null;
  disabled?: boolean;
  rowSpanMode?: boolean;
  onToggleRowSpanMode?: () => void;
  /** Hide F/K/U… — page bar only shows Højde when SHOW_ROW_SPAN_UI. */
  showMarks?: boolean;
  className?: string;
}

type FormatAction =
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "superscript"
  | "subscript";

const BUTTONS: {
  action: FormatAction;
  label: string;
  title: string;
  mark: string;
}[] = [
  { action: "bold", label: "F", title: "Fed (Ctrl/⌘+B)", mark: "bold" },
  { action: "italic", label: "K", title: "Kursiv (Ctrl/⌘+I)", mark: "italic" },
  {
    action: "underline",
    label: "U",
    title: "Understreget (Ctrl/⌘+U)",
    mark: "underline",
  },
  {
    action: "strike",
    label: "S",
    title: "Gennemstreget",
    mark: "strike",
  },
  {
    action: "superscript",
    label: "x²",
    title: "Hævet skrift",
    mark: "superscript",
  },
  {
    action: "subscript",
    label: "x₂",
    title: "Sænket skrift",
    mark: "subscript",
  },
];

function runFormat(editor: Editor, action: FormatAction) {
  if (editor.isDestroyed) return;
  const chain = editor.chain().focus();
  switch (action) {
    case "bold":
      chain.toggleBold().run();
      break;
    case "italic":
      chain.toggleItalic().run();
      break;
    case "underline":
      chain.toggleUnderline().run();
      break;
    case "strike":
      chain.toggleStrike().run();
      break;
    case "superscript":
      chain.toggleSuperscript().run();
      break;
    case "subscript":
      chain.toggleSubscript().run();
      break;
  }
}

function rememberColorRange(
  editor: Editor,
  ref: { current: { from: number; to: number } | null },
) {
  const { from, to } = editor.state.selection;
  ref.current = { from, to };
}

/** Prefer a live non-empty selection; else the range saved on toolbar mousedown. */
function resolveColorRange(
  editor: Editor,
  saved: { from: number; to: number } | null,
): { from: number; to: number } {
  const live = editor.state.selection;
  if (live.from !== live.to) return { from: live.from, to: live.to };
  if (saved && saved.from !== saved.to) return saved;
  return { from: live.from, to: live.to };
}

/** Button menu for manuscript text marks. Keeps editor focus via mousedown. */
export function FormatToolbar({
  editor,
  disabled,
  rowSpanMode = false,
  onToggleRowSpanMode,
  showMarks = true,
  className,
}: FormatToolbarProps) {
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const update = () => {
      if (editor.isDestroyed) return;
      bump();
    };
    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
    };
  }, [editor]);

  const liveEditor =
    editor && !editor.isDestroyed && editor.schema ? editor : null;
  const inactive = disabled || !liveEditor;
  const showRowSpanToggle = SHOW_ROW_SPAN_UI && Boolean(onToggleRowSpanMode);
  const showModes = showRowSpanToggle;
  const colorRangeRef = useRef<{ from: number; to: number } | null>(null);
  const colorBtnRef = useRef<HTMLButtonElement>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [palettePos, setPalettePos] = useState({ left: 0, top: 0 });
  const textStyleColor = liveEditor?.getAttributes("textStyle")?.color;
  const textColor =
    typeof textStyleColor === "string" ? textStyleColor : undefined;
  const colorHex = cssColorToHex(textColor) ?? DEFAULT_INK;
  const hasUniformColor = Boolean(cssColorToHex(textColor));
  const hasAnyColor = Boolean(liveEditor && selectionHasColorMark(liveEditor));
  /** Active when uniform custom color OR mixed colors in the selection. */
  const hasCustomColor = hasUniformColor || hasAnyColor;

  useEffect(() => {
    if (!paletteOpen) return;
    const btn = colorBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setPalettePos({
        left: Math.min(rect.left, window.innerWidth - 168),
        top: rect.bottom + 4,
      });
    }
    function onPointerDown(e: PointerEvent) {
      if (colorBtnRef.current?.contains(e.target as Node)) return;
      if (paletteRef.current?.contains(e.target as Node)) return;
      setPaletteOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPaletteOpen(false);
    }
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [paletteOpen]);

  function applyTextColor(color: string | null) {
    if (!liveEditor || disabled) return;
    const range = resolveColorRange(liveEditor, colorRangeRef.current);
    applyColorToRange(liveEditor, range, color);
    colorRangeRef.current = null;
    setPaletteOpen(false);
  }

  return (
    <div
      className={`sm-format-toolbar${className ? ` ${className}` : ""}`}
      role="toolbar"
      aria-label={showMarks ? "Tekstformatering" : "Rækkehøjde"}
      onMouseDown={(e) => {
        // Keep TipTap selection when clicking format buttons
        e.preventDefault();
      }}
    >
      {showMarks
        ? BUTTONS.map((btn) => {
            const active = Boolean(liveEditor?.isActive(btn.mark));
            return (
              <button
                key={btn.action}
                type="button"
                className={active ? "is-active" : undefined}
                title={btn.title}
                aria-label={btn.title}
                aria-pressed={active}
                disabled={inactive}
                onClick={() => {
                  if (!liveEditor || disabled) return;
                  runFormat(liveEditor, btn.action);
                }}
              >
                <span className={`sm-format-label sm-format-${btn.action}`}>
                  {btn.label}
                </span>
              </button>
            );
          })
        : null}
      {showMarks ? (
        <>
          <button
            ref={colorBtnRef}
            type="button"
            className={`sm-format-color${hasCustomColor ? " is-active" : ""}${
              paletteOpen ? " is-open" : ""
            }`}
            title="Tekstfarve"
            aria-label="Tekstfarve"
            aria-haspopup="dialog"
            aria-expanded={paletteOpen}
            disabled={inactive}
            onMouseDown={() => {
              if (!liveEditor) return;
              rememberColorRange(liveEditor, colorRangeRef);
            }}
            onClick={() => {
              if (inactive) return;
              setPaletteOpen((open) => !open);
            }}
          >
            <span
              className="sm-format-color-letter"
              aria-hidden
              style={hasUniformColor ? { color: colorHex } : undefined}
            >
              A
            </span>
            <span
              className="sm-format-color-bar"
              style={{
                background: hasUniformColor
                  ? colorHex
                  : hasAnyColor
                    ? `linear-gradient(90deg, #8b1a3a, #1e4d8c, ${DEFAULT_INK})`
                    : colorHex,
              }}
            />
          </button>
          {paletteOpen
            ? createPortal(
                <div
                  ref={paletteRef}
                  className="sm-format-color-palette"
                  role="dialog"
                  aria-label="Tekstfarve"
                  style={{ left: palettePos.left, top: palettePos.top }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="sm-ctx-swatches">
                    {TEXT_PRESET_COLORS.map((color) => {
                      const label =
                        PRESET_COLOR_LABELS[color] ?? `Vælg ${color}`;
                      return (
                        <button
                          key={color}
                          type="button"
                          className={`sm-ctx-swatch${
                            hasUniformColor &&
                            colorHex.toLowerCase() === color
                              ? " is-active"
                              : ""
                          }`}
                          style={{ background: color }}
                          title={label}
                          aria-label={label}
                          onClick={() => applyTextColor(color)}
                        />
                      );
                    })}
                  </div>
                </div>,
                document.body,
              )
            : null}
          <button
            type="button"
            title="Nulstil tekstfarve"
            aria-label="Nulstil tekstfarve"
            disabled={inactive || !hasAnyColor}
            onMouseDown={() => {
              if (!liveEditor) return;
              rememberColorRange(liveEditor, colorRangeRef);
            }}
            onClick={() => applyTextColor(null)}
          >
            <span className="sm-format-label">∅</span>
          </button>
        </>
      ) : null}
      {showModes ? (
        <>
          {showMarks ? (
            <span className="sm-format-toolbar-sep" aria-hidden />
          ) : null}
          {showRowSpanToggle ? (
            <button
              type="button"
              className={
                rowSpanMode
                  ? "is-active sm-format-mode-btn"
                  : "sm-format-mode-btn"
              }
              title={
                rowSpanMode
                  ? "Slå fra for at skrive uden at ændre rækkehøjde"
                  : "Træk i underkanten af billed- eller lydsiden for at dække flere rækker"
              }
              aria-label="Udvid rækkehøjde"
              aria-pressed={rowSpanMode}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onToggleRowSpanMode?.();
              }}
            >
              Højde
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
